import {useEffect, useState} from "react";
import {FieldErrors, useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Dungeon} from "@/dungeons/model/Dungeon.ts";
import {
    DungeonFormInput,
    DungeonFormOutput,
    dungeonSchema,
    SaveDungeonGeometryRequest,
    toSaveDungeonGeometryRequest,
} from "@/dungeons/admin/form/DungeonFormSchema.ts";
import {Form} from "@/components/ui/form.tsx";
import HiddenField from "@/components/forms/HiddenField.tsx";
import FormToolbar from "@/components/management/FormToolbar.tsx";
import {SaveState} from "@/components/buttons/SaveButton.tsx";
import DungeonUnifiedEditor from "@/dungeons/editor/shared/DungeonUnifiedEditor.tsx";
import {isAxiosError} from "axios";
import {DungeonEditorHistoryProvider} from "@/dungeons/editor/history/DungeonEditorHistory.tsx";
import DungeonEditorHistoryControls from "@/dungeons/editor/history/DungeonEditorHistoryControls.tsx";
import {DungeonEditorSessionProvider} from "@/dungeons/editor/shared/DungeonEditorSession.tsx";
import DungeonEditorLevelSelect from "@/dungeons/editor/shared/DungeonEditorLevelSelect.tsx";
import {notifyError} from "@/api/ErrorNotification.ts";
import EditorLightingToggle from "@/dungeons/editor/lighting/EditorLightingToggle.tsx";

export default function DungeonGeometryForm({dungeon, onSubmit, onSaved, onCancel, onDirtyChange}: {
    dungeon: Dungeon;
    onSubmit: (request: SaveDungeonGeometryRequest) => Promise<Dungeon>;
    onSaved: (dungeon: Dungeon) => void;
    onCancel: () => void;
    onDirtyChange: (dirty: boolean) => void;
}) {
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [error, setError] = useState<string>();
    const form = useForm<DungeonFormInput, unknown, DungeonFormOutput>({
        resolver: zodResolver(dungeonSchema),
        mode: "onBlur",
        defaultValues: dungeon,
    });
    const dungeonName = useWatch({control: form.control, name: "name"});
    const levels = useWatch({control: form.control, name: "levels"});
    const displayName = (typeof dungeonName === "string" ? dungeonName.trim() : "") || dungeon?.name;
    const title = `${displayName} · Editor`;
    const revisions = levels?.map(level => level.geometryRevision) ?? [];
    const revisionLabel = revisions.length === 0
        ? "Noch keine Levelrevision"
        : revisions.length === 1
            ? `Geometrie-Revision r${revisions[0]}`
            : `${revisions.length} Level · Geometrie-Revisionen ${revisions.map(revision => `r${revision}`).join(" / ")}`;

    useEffect(() => onDirtyChange(form.formState.isDirty), [form.formState.isDirty, onDirtyChange]);

    const onValid = async (data: DungeonFormOutput) => {
        setSaveState("saving");
        setError(undefined);
        try {
            const saved = await onSubmit(toSaveDungeonGeometryRequest(data));
            form.reset(saved);
            setSaveState("success");
            onSaved(saved);
        } catch (reason) {
            setSaveState("error");
            if (isAxiosError(reason) && reason.response?.status === 409) {
                setError("Der Dungeon wurde zwischenzeitlich geändert. Lade ihn neu, bevor du deine Änderungen erneut anwendest.");
            } else notifyError(reason, "Der Dungeon konnte nicht gespeichert werden.");
        } finally {
            setTimeout(() => setSaveState("idle"), 1500);
        }
    };

    const onInvalid = (errors: FieldErrors<DungeonFormInput>) => {
        console.error("Form errors", errors);
        setSaveState("error");
        setError("Bitte überprüfe die markierten Eingaben.");
        setTimeout(() => setSaveState("idle"), 1500);
    };

    return (
        <Form {...form}>
            <DungeonEditorHistoryProvider>
                <DungeonEditorSessionProvider>
                <form onSubmit={form.handleSubmit(onValid, onInvalid)} className="flex h-full min-h-0 flex-col">
                    <HiddenField name="id"/>
                    <div className="flex min-h-0 flex-1 flex-col gap-2">
                        <FormToolbar title={title} subtitle={revisionLabel}
                                     actions={<>
                                         <DungeonEditorLevelSelect/>
                                         <EditorLightingToggle/>
                                         <DungeonEditorHistoryControls/>
                                     </>}
                                     saveState={saveState}
                                     saveDisabled={!form.formState.isDirty || saveState === "saving"}
                                     onCancel={onCancel}/>
                        {error && <p role="alert"
                                     className="shrink-0 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                            {error}
                        </p>}
                        <div className="min-h-0 flex-1"><DungeonUnifiedEditor/></div>
                    </div>
                </form>
                </DungeonEditorSessionProvider>
            </DungeonEditorHistoryProvider>
        </Form>
    );
}
